import React from "react";
import { FieldValues, useController } from "react-hook-form";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import UserInitials from "components/comments/UserInitials";
import { useCompany } from "lib/hooks/api/company/useCompany";
import { Select, SelectProps } from "./Inputs";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options">;

type Option = {
  label: string;
  value: string;
  renderOption: React.ReactNode;
  renderSelectedOption: React.ReactNode;
};

function CompanyUserOption({
  id,
  icon,
  text,
}: {
  id: string;
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <CompanyUserSelectOption key={id}>
      {icon}
      <Text>{text}</Text>
    </CompanyUserSelectOption>
  );
}

export default function CompanyUserSelect<T extends FieldValues>(props: FieldProps<T>) {
  const { field } = useController(props);

  const { data: company, isLoading: isLoadingCompany } = useCompany();

  const options: Option[] =
    company?.users.map((user) => ({
      value: user.guid,
      label: `${user.firstName} ${user.lastName}`,
      renderOption: (
        <CompanyUserOption
          id={user.guid}
          text={`${user.firstName} ${user.lastName}`}
          icon={
            <UserInitials
              firstName={user?.firstName}
              lastName={user?.lastName}
              showTooltip={false}
              type="extra-small"
            />
          }
        />
      ),
      renderSelectedOption: (
        <Flex alignItems="center" gap={8}>
          <UserInitials
            firstName={user?.firstName}
            lastName={user?.lastName}
            showTooltip={false}
            type="extra-small"
          />
          <Text>{`${user.firstName} ${user.lastName}`}</Text>
        </Flex>
      ),
    })) || [];

  return (
    <Select
      {...props}
      allowClear
      placeholder="Select user"
      options={options}
      value={field.value}
      loading={isLoadingCompany}
      optionRender={({ data }) => data.renderOption}
      optionLabelProp={"renderSelectedOption"}
    />
  );
}

const CompanyUserSelectOption = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  padding: 8px 0;
`;
